/**
 * Google Tag Manager Service
 *
 * Ce service fournit des fonctionnalités pour interagir avec Google Tag Manager (GTM)
 * et la dataLayer. Il permet de pousser des événements, des données utilisateur
 * et de gérer le consentement.
 */

// Google Tag Manager ID used by the application
export const GTM_ID = 'GTM-WWWVRBWQ';

// Type definitions for DataLayer events
interface DataLayerEvent {
  event: string;
  [key: string]: unknown;
}

// Define DataLayer item structure
type DataLayerItem = DataLayerEvent | Record<string, unknown>;

declare global {
  interface Window {
    dataLayer: DataLayerItem[];
    gtmDidInit?: boolean;
  }
}

/**
 * Vérifie si dataLayer est disponible dans le navigateur
 */
const isDataLayerAvailable = (): boolean => {
  return typeof window !== 'undefined' && Array.isArray(window.dataLayer);
};

/**
 * Initialise dataLayer si elle n'existe pas encore
 */
const initDataLayer = (): void => {
  if (typeof window !== 'undefined' && !Array.isArray(window.dataLayer)) {
    window.dataLayer = [];
  }
};

/**
 * Pousse un événement dans la dataLayer
 * @param eventName - Nom de l'événement
 * @param data - Données supplémentaires associées à l'événement
 */
export const pushEvent = (
  eventName: string,
  data: Record<string, unknown> = {},
): void => {
  initDataLayer();

  if (isDataLayerAvailable()) {
    const eventData: DataLayerEvent = {
      event: eventName,
      ...data,
    };

    window.dataLayer.push(eventData);
  }
};

/**
 * Pousse des données utilisateur dans la dataLayer
 * @param userData - Données utilisateur à pousser
 */
export const pushUserData = (userData: Record<string, unknown>): void => {
  initDataLayer();

  if (isDataLayerAvailable()) {
    window.dataLayer.push({
      user: userData,
    });
  }
};

/**
 * Configure le consentement utilisateur pour le RGPD
 * @param consents - Objet de consentement avec les clés comme catégories et valeurs comme état de consentement
 */
export const setConsent = (consents: Record<string, boolean>): void => {
  pushEvent('consent_update', { consent_settings: consents });
};

/**
 * Trace une page vue pour le SPA
 * @param path - Chemin de la page
 * @param title - Titre de la page
 */
export const trackPageView = (path: string, title: string): void => {
  pushEvent('page_view', {
    page_path: path,
    page_title: title,
  });
};

/**
 * Initialise GTM programmatiquement (alternative à l'inclusion dans le HTML)
 * Note: Cette méthode est une solution de secours si vous ne pouvez pas ajouter les scripts directement dans index.html
 */
export const initGTM = (): void => {
  if (typeof window !== 'undefined' && !window.gtmDidInit) {
    initDataLayer();

    window.gtmDidInit = true;
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;

    document.head.appendChild(script);

    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
  }
};

// Exporter le service comme objet
const GTMService = {
  initDataLayer,
  pushEvent,
  pushUserData,
  setConsent,
  trackPageView,
  initGTM,
  GTM_ID,
};

export default GTMService;
